import React, {useCallback, useEffect, useState, useRef} from 'react'
import {Dialog, Select, MenuItem, TextField, Snackbar} from "@mui/material";
import {AxiosError} from "axios";
import {useQuery} from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import HeaderSpacer from "@/view/layout/HeaderSpacer";
import BottomButton from "@/view/common/component/button/BottomButton";
import GameService, {GameData, GameItem} from "@/service/GameService";
import StringUtil from "@/common/StringUtil";
import StorageUtil from "@/common/StorageUtil";
import {history} from "@/view/common/component/History";
import Confirm from "@/view/common/popup/Confirm";
import {USER_ID} from "@/common/Constant";
import PlaceService, { PlaceData } from "@/service/PlaceService";


export interface GameAddParam {
    onClose: () => void,
    onReturn: () => void,
    game: GameItem
}

const GameEditTab = (props: GameAddParam) => {
    const handleOverlayClick= useCallback(() => props.onClose(), [props.onClose])
    const handleReturnClick = useCallback(() => {
        props.onReturn()}, [props.onReturn])
    const dateRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false)
    const [places, setPlaces] = useState<PlaceData[]>([])
    const writerId = StorageUtil.getLocalStorage(USER_ID)

    const [gameItem, setGameItem] = useState({
        id: props.game.id,
        writerId: writerId,
        teamId: props.game.teamId,
        matchDt: props.game.matchDt ?? '',
        time: props.game.time ?? '',
        place: props.game.place ?? '',
        type: props.game.type ?? '',
        playersCountType: props.game.playersCountType ?? '',
        vsName: props.game.vsName ?? '',
    })


    const TIME_OPTIONS = [
        '20:00~22:00',
        '19:00~21:00',
        '18:00~20:00',
        '17:00~19:00',
        '16:00~18:00',
        '15:00~17:00',
        '14:00~16:00',
        '13:00~15:00',
        '12:00~14:00',
        '11:00~13:00',
        '10:00~12:00',
        '09:00~11:00',
        '08:00~10:00',
        '07:00~09:00',
        '06:00~08:00',
    ]

    const TYPE_OPTIONS = [
        { value: '자체전', label: '자체전' },
        { value: '상대전', label: '상대전' },
    ]

    const PLAYER_OPTIONS = [
        '11 VS 11',
        '10 VS 10',
    ]

    const [registItem, setRegistItem] = useState<GameItem>()
    const [isOpen, setIsOpen] = useState(false)
    let registFlag = false

    /** 뒤로가기 감지 */
    useEffect(() => {
        const unlisten = history.listen(({action}) => {
            if (action === 'POP') {
                checkIsChanged()
            }
        })
        return unlisten
    }, [])

    const checkIsChanged = () => {
        if (
            StringUtil.isNotEmpty(gameItem.matchDt) ||
            StringUtil.isNotEmpty(gameItem.place)
        ) {
            setIsOpen(true)
        } else {
            handleOverlayClick()
            props.onReturn()
        }
    }

    const inputChanged = (event: { target: { id: string; value: string } }) => {
        const {id, value} = event.target
        setGameItem({...gameItem, [id]: value})
    }

    /** 경기 등록 API */
    const {refetch: putGame} = useQuery<GameData, AxiosError>(
        ['putGame'],
        () => GameService.putGame(registItem, registItem.id),
        {
            onSuccess: () => {
                setOpen(true)
                handleReturnClick()
            }
        }
    )

    const updateGame = () => {
        setRegistItem({
            id: props.game.id,
            writerId: writerId,
            teamId: props.game.teamId,
            matchDt: gameItem.matchDt,
            time: gameItem.time,
            place: gameItem.place,
            type: gameItem.type,
            playersCountType: gameItem.playersCountType,
            vsName: gameItem.vsName
        })
    }

    useEffect(() => {
        if (!registItem) return
        if (registFlag) return

        registFlag = true
        putGame()
            .then(() => {
                registFlag = false
                return Promise<void>
            })
            .catch(console.log)
    }, [registItem])

    const isDisabled =
        gameItem.matchDt === '' ||
        gameItem.time === '' ||
        gameItem.place === '' ||
        gameItem.type === '' ||
        gameItem.playersCountType === '' ||
        (gameItem.type ==='상대전' && gameItem.vsName === '')

    useEffect(() => {
        loadPlaces()
    }, [])

    const loadPlaces = async () => {
        try {
            const list = await PlaceService.getPlaceList()
            setPlaces(list)
        } catch (e) {
            console.error(e)
        }
    }
    return (
        <>
            <div className={'game'}>
                <div className="mainInput">
                    {/* 경기 날짜 */}
                    <TextField
                        id="matchDt"
                        type="date"
                        variant="outlined"
                        fullWidth
                        className="b2_r mt-24px"
                        inputRef={dateRef}
                        value={gameItem.matchDt}
                        InputLabelProps={{ shrink: true }}
                        onClick={() => {
                            const input = dateRef.current as any
                            if (input?.showPicker) {
                                input.showPicker()
                            } else {
                                input?.focus()
                            }
                        }}
                        onChange={inputChanged}
                    />

                    {/* 시간 */}
                    <Select
                        value={gameItem.time}
                        displayEmpty
                        className="b2_r w-100p mt-16px"
                        onChange={(e) =>
                            setGameItem({...gameItem, time: e.target.value})
                        }
                    >
                        <MenuItem value="" disabled>시간</MenuItem>
                        {TIME_OPTIONS.map(time => (
                            <MenuItem key={time} value={time}>{time}</MenuItem>
                        ))}
                    </Select>

                    {/* 장소 */}
                    <Select
                        value={gameItem.place}
                        displayEmpty
                        className="b2_r w-100p mt-16px"
                        onChange={(e) =>
                            setGameItem({...gameItem, place: e.target.value})
                        }
                    >
                        <MenuItem value="" disabled>장소</MenuItem>
                        {places.map(place => (
                            <MenuItem
                                key={place.id}
                                value={place.name}   // ✅ 저장은 name
                            >
                                {place.name}
                            </MenuItem>
                        ))}
                    </Select>

                    {/* 경기 타입 */}
                    <Select
                        value={gameItem.type}
                        displayEmpty
                        className="b2_r w-100p mt-16px"
                        onChange={(e) =>
                            setGameItem({...gameItem, type: e.target.value})
                        }
                    >
                        <MenuItem value="" disabled>타입</MenuItem>
                        {TYPE_OPTIONS.map(type => (
                            <MenuItem key={type.value} value={type.value}>
                                {type.label}
                            </MenuItem>
                        ))}
                    </Select>

                    {/* 상대팀 이름 (상대전일 때만 활성화) */}
                    {gameItem.type === '상대전' && (
                        <TextField
                            id="vsName"
                            variant="outlined"
                            fullWidth
                            className="b2_r w-100p mt-16px"
                            placeholder="상대팀 이름"
                            value={gameItem.vsName}
                            inputProps={{ maxLength: 30 }}
                            onChange={inputChanged}
                        />
                    )}


                    {/* 인원 */}
                    <Select
                        value={gameItem.playersCountType}
                        displayEmpty
                        className="b2_r w-100p mt-16px"
                        onChange={(e) =>
                            setGameItem({...gameItem, playersCountType: e.target.value})
                        }
                    >
                        <MenuItem value="" disabled>인원 선택</MenuItem>
                        {PLAYER_OPTIONS.map(cnt => (
                            <MenuItem key={cnt} value={cnt}>{cnt}</MenuItem>
                        ))}
                    </Select>
                </div>


                <BottomButton
                    className="primary"
                    disabled={isDisabled}
                    title="저장"
                    onClick={updateGame}
                />
            </div>
            <Confirm
                content={
                    <span>
                        작성 중인 내용이 사라집니다<br/>
                        그래도 나가시겠습니까?
                    </span>
                }
                leftBtnContent="아니오"
                rightBtnContent="네"
                isOpen={isOpen}
                onClick={() => setIsOpen(false)}
                onClose={() => props.onClose()}
            />

            <Snackbar
                open={open}
                autoHideDuration={1000}
                onClose={() => setOpen(false)}
                sx={{
                    position: 'fixed',
                    top: '20%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                }}
            >
                <Alert severity="success" variant="filled"></Alert>
            </Snackbar>
        </>
    )
}

export default GameEditTab
